'use client';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import type Hls from 'hls.js';
import { type ComponentProps, forwardRef, memo, useEffect, useMemo, useRef } from 'react';

import { type Clip } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';
import { loadHls } from '@air/utils-video';

import { usePlayerStore } from './PlayerContextProvider';

export type PlayerMediaProps = ComponentProps<'video' | 'audio'> & {
  clip?: Clip;
  type: 'audio' | 'video';
};

export const PlayerMedia = memo(
  forwardRef<HTMLAudioElement | HTMLVideoElement, PlayerMediaProps>(({ clip, type, ...restOfProps }, forwardedRef) => {
    const hlsRef = useRef<Hls | null>(null);
    const id = usePlayerStore((state) => state.id);
    const setHlsInstance = usePlayerStore((state) => state.setHlsInstance);
    const setVideoElement = usePlayerStore((state) => state.setVideoElement);
    const videoElement = usePlayerStore((state) => state.videoElement, {
      dangerouslyIncludeEventListeners: true,
    });
    const composedRefs = useComposedRefs(forwardedRef, setVideoElement);

    useEffect(() => {
      const loadVideo = async () => {
        try {
          hlsRef.current?.destroy();
          hlsRef.current = await loadHls({
            src: clip?.assets.video,
            videoElement,
          });
          setHlsInstance(hlsRef.current);
        } catch (error) {
          console.error(error);

          reportErrorToBugsnag({
            context: 'Failed to load HLS video in VideoPlayer',
            error,
            metadata: {
              data: { src: clip?.assets.video },
              key: 'Data',
            },
          });
        }
      };

      loadVideo();

      return () => {
        hlsRef.current?.destroy();
        setHlsInstance(null);
      };
    }, [clip?.assets.video, setHlsInstance, videoElement]);

    const ElementType = useMemo(() => (type === 'audio' ? 'audio' : 'video'), [type]);

    return <ElementType data-testid="PLAYER_MEDIA" {...restOfProps} key={id} ref={composedRefs} />;
  }),
);

PlayerMedia.displayName = 'PlayerMedia';
